import { Fragment, useState, type ReactNode } from 'react';
import { useDismissable } from '../../lib/hooks';

export interface MenuItem {
  key: string;
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  /** Draws a 1px separator above this item. */
  separatorBefore?: boolean;
  onSelect: () => void;
}

interface ActionMenuProps {
  items: MenuItem[];
  label?: string;
  /** Flip the popover upwards for rows near the bottom of the viewport. */
  align?: 'down' | 'up';
}

/**
 * The "..." row action menu. Mirrors the Figma "More Pop_up" component:
 * 8px radius, 1px #ebebeb border, 10/8 padding, 30px tall items, soft shadow.
 */
export function ActionMenu({ items, label = 'More actions', align = 'down' }: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useDismissable(open, () => setOpen(false));

  return (
    <div className="menu-anchor" ref={ref}>
      <button
        type="button"
        className="icon-button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="5" r="1.6" fill="currentColor" />
          <circle cx="12" cy="12" r="1.6" fill="currentColor" />
          <circle cx="12" cy="19" r="1.6" fill="currentColor" />
        </svg>
      </button>

      {open ? (
        <div className={`menu ${align === 'up' ? 'menu--up' : ''}`} role="menu">
          {items.map((item) => (
            <Fragment key={item.key}>
              {item.separatorBefore ? <div className="menu__separator" role="separator" /> : null}
              <button
                type="button"
                role="menuitem"
                className={`menu__item ${item.danger ? 'menu__item--danger' : ''}`.trim()}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
              >
                {item.icon}
                {item.label}
              </button>
            </Fragment>
          ))}
        </div>
      ) : null}
    </div>
  );
}
